import React, { useEffect, useMemo, useState } from "react"
import { apiFetch } from "../utils/apiFetch"
import {
  getInitialEndoForm,
  getInitialPerioForm,
  getInitialSurgeryForm,
  getInitialOrthoForm,
  getInitialPediatricForm,
} from "../features/clinical-history/components/ClinicalHistoryHelpers"
import {
  EndoHistoryModal,
  PerioHistoryModal,
  SurgeryHistoryModal,
  OrthoHistoryModal,
  PediatricHistoryModal,
} from "../features/clinical-history/components/ClinicalHistoryModals"

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

function getInitialFormBySpecialty(type) {
  switch (type) {
    case "ENDO":
      return getInitialEndoForm()
    case "PERIO":
      return getInitialPerioForm()
    case "SURGERY":
      return getInitialSurgeryForm()
    case "ORTHO":
      return getInitialOrthoForm()
    case "PEDIATRIC":
      return getInitialPediatricForm()
    default:
      return {}
  }
}

function buildInitialFormFromCase(specialtyType, caseDetail) {
  const tooth = caseDetail?.teeth?.[0]?.tooth || ""
  const chiefReason = caseDetail?.chiefReason || ""

  switch (specialtyType) {
    case "ENDO":
      return {
        ...getInitialEndoForm(),
        tooth,
        chiefComplaint: chiefReason,
      }

    case "SURGERY":
      return {
        ...getInitialSurgeryForm(),
        tooth,
        surgicalDiagnosis: chiefReason,
        procedureType: caseDetail?.title || "",
      }

    case "ORTHO":
      return {
        ...getInitialOrthoForm(),
        chiefComplaint: chiefReason,
        treatmentObjectives:
          caseDetail?.title && caseDetail.title !== "Caso de Ortodoncia"
            ? caseDetail.title
            : "",
      }

    case "PERIO":
      return {
        ...getInitialPerioForm(),
        chiefComplaint: chiefReason,
        periodontalDiagnosis:
          caseDetail?.title && caseDetail.title !== "Caso de Periodoncia"
            ? caseDetail.title
            : "",
      }

    case "PEDIATRIC":
      return {
        ...getInitialPediatricForm(),
        chiefComplaint: chiefReason,
        treatmentPlan:
          caseDetail?.title && caseDetail.title !== "Caso de OdontopediatrÃ­a"
            ? caseDetail.title
            : "",
      }

    default:
      return getInitialFormBySpecialty(specialtyType)
  }
}

function validateFormBySpecialty(type, form) {
  switch (type) {
    case "ENDO":
      return form.tooth?.trim() ? "" : "Indica la pieza dental."
    case "PERIO":
      return form.chiefComplaint?.trim() || form.periodontalDiagnosis?.trim()
        ? ""
        : "Escribe el motivo periodontal de consulta."
    case "SURGERY":
      return form.surgicalDiagnosis?.trim() || form.procedureType?.trim()
        ? ""
        : "Escribe el diagnÃ³stico quirÃºrgico o el procedimiento."
    case "ORTHO":
      return form.chiefComplaint?.trim() || form.treatmentObjectives?.trim()
        ? ""
        : "Escribe el motivo ortodÃ³ntico o los objetivos del tratamiento."
    case "PEDIATRIC":
      return form.chiefComplaint?.trim() || form.treatmentPlan?.trim()
        ? ""
        : "Escribe el motivo pediÃ¡trico o el plan de tratamiento."
    default:
      return "Tipo clÃ­nico no soportado."
  }
}

function buildEvolutionTitle(type, form) {
  switch (type) {
    case "ENDO":
      return `EvoluciÃ³n Endodoncia${form.tooth ? ` Â· Pieza ${form.tooth}` : ""}`
    case "PERIO":
      return "EvoluciÃ³n Periodoncia"
    case "SURGERY":
      return `EvoluciÃ³n CirugÃ­a${form.tooth ? ` Â· Pieza ${form.tooth}` : ""}`
    case "ORTHO":
      return "EvoluciÃ³n Ortodoncia"
    case "PEDIATRIC":
      return "EvoluciÃ³n OdontopediatrÃ­a"
    default:
      return "EvoluciÃ³n clÃ­nica"
  }
}

function buildEvolutionSummary(type, form) {
  switch (type) {
    case "ENDO":
      return [
        form.tooth ? `Pieza ${form.tooth}` : "",
        form.pulpDiagnosis || "",
        form.apicalDiagnosis || "",
      ]
        .filter(Boolean)
        .join(" Â· ")

    case "PERIO":
      return [
        form.chiefComplaint || "",
        form.periodontalDiagnosis || "",
        form.riskLevel ? `Riesgo ${form.riskLevel}` : "",
      ]
        .filter(Boolean)
        .join(" Â· ")

    case "SURGERY":
      return [
        form.procedureType || "",
        form.tooth ? `Pieza ${form.tooth}` : "",
        form.surgicalArea || "",
      ]
        .filter(Boolean)
        .join(" Â· ")

    case "ORTHO":
      return [form.skeletalClass || "", form.treatmentObjectives || ""]
        .filter(Boolean)
        .join(" Â· ")

    case "PEDIATRIC":
      return [
        form.companionRelation ? `AcompaÃ±ante ${form.companionRelation}` : "",
        form.cariesRisk ? `Riesgo ${form.cariesRisk}` : "",
        form.treatmentPlan || "",
      ]
        .filter(Boolean)
        .join(" Â· ")

    default:
      return ""
  }
}

export default function ClinicalHistorySpecialtyEvolutionEditorPage({
  clinicId,
  patientId,
  caseId,
  specialtyType,
  isReadOnly,
  readOnlyReason,
  onBackToCase,
}) {
  const [caseDetail, setCaseDetail] = useState(null)
  const [loadingCase, setLoadingCase] = useState(true)
  const [form, setForm] = useState(() => getInitialFormBySpecialty(specialtyType))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const loadCaseDetail = async () => {
      try {
        if (!caseId || !clinicId) {
          setLoadingCase(false)
          return
        }

        setLoadingCase(true)

        const res = await apiFetch(
          `${API_BASE}/clinical-specialty-cases/${caseId}?clinicId=${clinicId}`
        )

        const data = await res.json().catch(() => null)

        if (!res.ok) {
          throw new Error(data?.message || "No se pudo cargar el caso.")
        }

        setCaseDetail(data)
        setForm(buildInitialFormFromCase(specialtyType, data))
      } catch (err) {
        alert(err?.message || "No se pudo cargar el contexto del caso.")
      } finally {
        setLoadingCase(false)
      }
    }

    loadCaseDetail()
  }, [caseId, clinicId, specialtyType])

  const commonProps = useMemo(
    () => ({
      open: true,
      onClose: onBackToCase,
      form,
      setForm,
      saving,
      onSave: async () => {
        const validationError = validateFormBySpecialty(specialtyType, form)

        if (validationError) {
          alert(validationError)
          return
        }

        try {
          setSaving(true)

          const res = await apiFetch(
            `${API_BASE}/clinical-specialty-cases/${caseId}/evolutions?clinicId=${clinicId}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                clinicId,
                patientId,
                title: buildEvolutionTitle(specialtyType, form),
                summary: buildEvolutionSummary(specialtyType, form),
                status: "REGISTERED",
                formData: form,
              }),
            }
          )

          const data = await res.json().catch(() => null)

          if (!res.ok) {
            throw new Error(data?.message || "No se pudo registrar la evoluciÃ³n.")
          }

          alert("EvoluciÃ³n registrada correctamente.")
          onBackToCase()
        } catch (err) {
          alert(err?.message || "No se pudo registrar la evoluciÃ³n.")
        } finally {
          setSaving(false)
        }
      },
      latestInstance: caseDetail,
      isReadOnly,
      readOnlyReason,
    }),
    [
      caseDetail,
      caseId,
      clinicId,
      form,
      isReadOnly,
      onBackToCase,
      patientId,
      readOnlyReason,
      saving,
      specialtyType,
    ]
  )

  if (loadingCase) {
    return null
  }

  switch (specialtyType) {
    case "ENDO":
      return <EndoHistoryModal {...commonProps} />
    case "PERIO":
      return <PerioHistoryModal {...commonProps} />
    case "SURGERY":
      return <SurgeryHistoryModal {...commonProps} />
    case "ORTHO":
      return <OrthoHistoryModal {...commonProps} />
    case "PEDIATRIC":
      return <PediatricHistoryModal {...commonProps} />
    default:
      return null
  }
}
